import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Tenant,
  Service,
  Staff,
  Appointment,
  ApiError,
  AdminClient,
  TimeOff,
  fetchSession,
  fetchAdminAppointments,
  fetchStaffAppointments,
  fetchTenantDetail,
  fetchAdminClients,
  fetchAdminTimeOff,
  createAdminAppointment,
  updateAppointment,
  updateStaffAppointment,
} from '../../api';
import { useAuth } from '../../context/AuthContext';
import { format } from 'date-fns';
import { toSalonTime, fromSalonWallClock } from '../../lib/datetime';
import { findTimeOffClash, formatTimeOffSpan } from '../../lib/timeOff';
import {
  Calendar as CalendarIcon,
  Users,
  DollarSign,
  X,
  Pencil,
  Eye,
  EyeOff,
  ArrowLeft,
  Loader2,
  MessageCircle,
  CalendarOff,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import PhoneField from '../../components/PhoneField';
import AdminCalendar from '../../components/AdminCalendar';
import AdminSalonSettings from '../../components/AdminSalonSettings';
import AdminStaffSettings from '../../components/AdminStaffSettings';
import AdminTimeOff from '../../components/AdminTimeOff';
import AdminAccountSettings from '../../components/AdminAccountSettings';
import AdminAppearanceSettings from '../../components/AdminAppearanceSettings';
import ConfirmDialog from '../../components/ConfirmDialog';
import TodayAgenda from '../../components/TodayAgenda';
import AdminAnalytics from '../../components/AdminAnalytics';
import AdminClients from '../../components/AdminClients';
import AdminPasswordReset from '../../components/auth/AdminPasswordReset';
import AdminNav from '@/src/components/admin/AdminNav';
import AdminSettingsLayout from '@/src/components/admin/AdminSettingsLayout';
import { hashForTab, tabFromHash, type Tab } from '@/src/components/admin/navConfig';
import { STATUS_TEXT } from '@/src/lib/statusColors';
import ThemeToggle from '../../components/ThemeToggle';

const SUPPORT_WHATSAPP_NUMBER = import.meta.env.VITE_SUPPORT_WHATSAPP_NUMBER || '+96176472623';
const SUPPORT_WHATSAPP_URL = `https://wa.me/${SUPPORT_WHATSAPP_NUMBER.replace(/[^0-9]/g, '')}`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [services, setServices] = useState<Service[]>([]);
  const [staffList, setStaffList] = useState<Staff[]>([]);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  // 'owner' sees everything; 'staff' (a worker) sees only their own calendar.
  const [role, setRole] = useState<'owner' | 'staff'>('owner');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newAppDate, setNewAppDate] = useState<string>('');
  const [newAppCustomer, setNewAppCustomer] = useState('');
  const [newAppPhone, setNewAppPhone] = useState<string | undefined>();
  // Set when the owner picks an existing walk-in from the suggestions, so the
  // booking attaches to that row. Cleared on any manual edit so a stale id can
  // never attach the wrong client — no id means a fresh walk-in is filed.
  const [newAppClientId, setNewAppClientId] = useState<string | null>(null);
  const [newAppService, setNewAppService] = useState('');
  const [newAppStaff, setNewAppStaff] = useState('');
  // The URL hash is the tab state — no second copy in component state, so
  // browser back/forward needs no listener of its own. Written through the
  // router rather than `window.location.hash`, which would leave every
  // `useLocation()` in the tree stale until the next popstate.
  const activeTab: Tab = tabFromHash(location.hash);
  const setActiveTab = (tab: Tab) => {
    const target = hashForTab(tab);
    // Re-picking the current tab shouldn't push a duplicate history entry.
    if (location.hash.replace(/^#/, '') !== target) {
      navigate({ hash: target });
    }
    // Salon is a long scroll; the next tab shouldn't inherit its scroll position.
    window.scrollTo({ top: 0 });
  };
  // The owner's own time-off rows, for the calendar banner. Worth the extra
  // request: the copy on the tenant detail response is the customer-facing one,
  // narrowed to salon-wide whole-day blocks inside 60 days, which left the owner
  // with no banner for a half-day, a worker's absence, or anything further out.
  const [timeOff, setTimeOff] = useState<TimeOff[]>([]);
  const [showRevenue, setShowRevenue] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState<Appointment | null>(null);
  const [isSupportModalOpen, setIsSupportModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editDateTime, setEditDateTime] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [formGeneralError, setFormGeneralError] = useState('');
  const [actionError, setActionError] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState<AdminClient[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const customerSearchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Monotonic id so a slow suggestion response can't replace results for text
  // the user has since changed.
  const suggestionReqRef = useRef(0);
  const customerInputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  // Go through AuthContext so the shared session (and the public header's
  // logged-in/out state) stays in sync with the dashboard.
  const { login, logout } = useAuth();

  // Login state
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  // Set when a worker opens their invite link (#set-password?email=...). Read
  // once, on mount: the link is how you arrive, not somewhere you navigate to.
  const [inviteEmail, setInviteEmail] = useState<string | null>(() => {
    const hash = window.location.hash;
    if (!hash.startsWith('#set-password')) return null;
    // Don't use URLSearchParams: it decodes "+" to a space, which corrupts
    // plus-addressed emails (john+salon@gmail.com). decodeURIComponent reverses
    // the link's encoding and leaves a literal "+" intact. A missing/empty email
    // yields null so we fall through to the normal login (no dead-end form).
    const match = (hash.split('?')[1] || '').match(/(?:^|&)email=([^&]*)/);
    if (!match) return null;
    try {
      return decodeURIComponent(match[1]) || null;
    } catch {
      return match[1] || null;
    }
  });

  useEffect(() => {
    // Check if already authenticated as an owner or a worker on mount.
    fetchSession().then((session) => {
      if ((session?.role === 'owner' || session?.role === 'staff') && session.tenant) {
        setRole(session.role);
        setTenant(session.tenant);
        setIsLoggedIn(true);
      } else {
        setIsLoggedIn(false);
      }
    });
  }, []);

  const loadDashboardData = useCallback(async () => {
    if (!tenant) return;
    try {
      const [appts, detail, timeOffRows] = await Promise.all([
        role === 'staff' ? fetchStaffAppointments() : fetchAdminAppointments(),
        fetchTenantDetail(tenant.slug),
        // Owner-only endpoint, so a worker must not ask: the 403 would land in
        // the catch below and sign them out of a dashboard they can use. They
        // get no banner rather than no dashboard.
        role === 'staff' ? Promise.resolve<TimeOff[]>([]) : fetchAdminTimeOff(),
      ]);
      setAppointments(appts);
      setServices(detail.services);
      setStaffList(detail.staff);
      setTimeOff(timeOffRows);
    } catch {
      setIsLoggedIn(false);
      setTenant(null);
    }
  }, [tenant, role]);

  useEffect(() => {
    if (isLoggedIn && tenant) {
      loadDashboardData();
    }
  }, [isLoggedIn, loadDashboardData, tenant]);

  useEffect(() => {
    if (!isModalOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        customerInputRef.current &&
        !customerInputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isModalOpen]);

  /**
   * The time off, if any, that will make the booking in the modal fail.
   *
   * Time off is a hard wall even for the owner: `allow_overlap` lets them
   * double-book a worker over an existing appointment, never book into an
   * absence, so a slot inside a block can only ever fail on submit — after five
   * fields of typing. This says so while there is still something to change.
   *
   * A warning, not a veto. It checks time off alone, while the server also
   * weighs business hours and existing bookings, so it can be silent on a
   * booking that still fails. Disabling the button on that approximation would
   * be the worse error: it would lock the owner out of a booking that was legal.
   */
  const timeOffClash = useMemo(
    () =>
      findTimeOffClash({
        rows: timeOff,
        startsAt: newAppDate,
        service: services.find((s) => String(s.id) === newAppService),
        staffId: newAppStaff ? parseInt(newAppStaff) : null,
        staff: staffList,
      }),
    [timeOff, newAppDate, newAppService, newAppStaff, services, staffList],
  );

  const handleCustomerInputChange = (value: string) => {
    setNewAppCustomer(value);
    setNewAppClientId(null);
    if (customerSearchTimeout.current) clearTimeout(customerSearchTimeout.current);
    if (value.trim().length < 2) {
      setCustomerSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    customerSearchTimeout.current = setTimeout(async () => {
      const reqId = ++suggestionReqRef.current;
      try {
        const res = await fetchAdminClients(value.trim(), undefined, true);
        if (reqId !== suggestionReqRef.current) return;
        setCustomerSuggestions(res.results.slice(0, 8));
        setShowSuggestions(true);
      } catch {
        if (reqId === suggestionReqRef.current) setCustomerSuggestions([]);
      }
    }, 300);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError('');
    try {
      const session = await login(username, password);
      if ((session.role === 'owner' || session.role === 'staff') && session.tenant) {
        setRole(session.role);
        setTenant(session.tenant);
        setIsLoggedIn(true);
      } else {
        setLoginError('Your account is not associated with a salon.');
        await logout();
      }
    } catch {
      setLoginError('Invalid credentials.');
    }
  };

  const handleLogout = async () => {
    await logout();
    setIsLoggedIn(false);
    setTenant(null);
    setAppointments([]);
  };

  if (isLoggedIn === null) {
    return (
      <div className="bg-canvas text-fg-muted flex min-h-screen items-center justify-center">
        Loading...
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <div className="bg-canvas flex min-h-screen flex-col items-center justify-center gap-6 p-4">
        {/* AdminNav (which hosts the usual toggle) isn't mounted yet, so
            without this an owner on a dark device meets a lit-up login card
            and no way out of it. */}
        <div className="absolute top-4 right-4">
          <ThemeToggle variant="desktop" />
        </div>
        <div className="border-default bg-surface w-full max-w-sm rounded-2xl border p-8 shadow-sm">
          <h1 className="text-fg mb-6 text-center text-2xl font-bold">
            {inviteEmail !== null
              ? 'Set your password'
              : showPasswordReset
                ? 'Reset your password'
                : 'Calendyz Pro Login'}
          </h1>
          {inviteEmail !== null ? (
            <AdminPasswordReset
              initialEmail={inviteEmail}
              initialStep="confirm"
              inviteMode
              onBackToLogin={() => {
                navigate({ hash: '' });
                setInviteEmail(null);
              }}
            />
          ) : showPasswordReset ? (
            <AdminPasswordReset onBackToLogin={() => setShowPasswordReset(false)} />
          ) : (
            <>
              {loginError && (
                <div className="border-danger/25 bg-danger-soft text-danger mb-4 rounded-xl border p-3 text-sm">
                  {loginError}
                </div>
              )}
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label
                    htmlFor="admin-login-id"
                    className="text-fg-secondary mb-1 block text-sm font-semibold"
                  >
                    Email
                  </label>
                  <input
                    id="admin-login-id"
                    type="email"
                    required
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="focus:ring-brand-600 border-default w-full rounded-lg border px-4 py-2 outline-none focus:border-transparent focus:ring-2"
                    placeholder="you@example.com"
                    autoComplete="email"
                  />
                </div>
                <div>
                  <label
                    htmlFor="admin-login-pw"
                    className="text-fg-secondary mb-1 block text-sm font-semibold"
                  >
                    Password
                  </label>
                  <input
                    id="admin-login-pw"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="focus:ring-brand-600 border-default w-full rounded-lg border px-4 py-2 outline-none focus:border-transparent focus:ring-2"
                    placeholder="password"
                    autoComplete="current-password"
                  />
                </div>
                <button
                  type="submit"
                  className="bg-brand-600 hover:bg-brand-700 w-full rounded-xl py-3 font-bold text-white transition-colors"
                >
                  Sign In
                </button>
                <div className="text-center text-sm">
                  <button
                    type="button"
                    className="text-accent hover:underline"
                    onClick={() => {
                      setLoginError('');
                      setShowPasswordReset(true);
                    }}
                  >
                    Forgot your password?
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
        <button
          onClick={() => navigate('/')}
          className="hover:text-accent text-fg-muted flex items-center gap-1.5 text-sm font-medium transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to home
        </button>
      </div>
    );
  }

  if (!tenant) return <div className="p-8">Loading dashboard...</div>;

  const isOwner = role === 'owner';
  // Workers only have the calendar, even if an old hash points elsewhere.
  const effectiveTab: Tab = isOwner ? activeTab : 'calendar';
  const refetchAppointments = () =>
    role === 'staff' ? fetchStaffAppointments() : fetchAdminAppointments();

  const handleAppointmentClick = (appointment: Appointment) => {
    setSelectedAppointment(appointment);
    setEditDateTime(format(toSalonTime(appointment.startTime), "yyyy-MM-dd'T'HH:mm"));
    setIsEditing(false);
    setActionError('');
  };

  const closeDetailModal = () => {
    setSelectedAppointment(null);
    setIsEditing(false);
    setActionError('');
  };

  const handleSaveDateTime = async () => {
    if (!selectedAppointment) return;
    setIsSaving(true);
    setActionError('');
    try {
      await updateAppointment(selectedAppointment.id, {
        startTime: fromSalonWallClock(editDateTime).toISOString(),
      });
      const appts = await refetchAppointments();
      setAppointments(appts);
      closeDetailModal();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Failed to update appointment.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelAppointment = async () => {
    if (!selectedAppointment) return;
    setIsSaving(true);
    setActionError('');
    try {
      if (role === 'staff') {
        await updateStaffAppointment(selectedAppointment.id, { status: 'cancelled' });
      } else {
        await updateAppointment(selectedAppointment.id, { status: 'cancelled' });
      }
      const appts = await refetchAppointments();
      setAppointments(appts);
      closeDetailModal();
    } catch (err) {
      setActionError(err instanceof ApiError ? err.message : 'Failed to cancel appointment.');
    } finally {
      setIsSaving(false);
      setShowCancelConfirm(false);
    }
  };

  // Clear every field the New Appointment modal owns. Called on open, on close,
  // and after a successful submit, so one booking's input never leaks into the
  // next — fields used to persist until the next successful submit.
  const resetAppointmentForm = () => {
    setNewAppCustomer('');
    setNewAppPhone(undefined);
    setNewAppClientId(null);
    setNewAppService('');
    setNewAppStaff('');
    setCustomerSuggestions([]);
    setShowSuggestions(false);
    setFormErrors({});
    setFormGeneralError('');
  };

  const closeAppointmentModal = () => {
    setIsModalOpen(false);
    resetAppointmentForm();
  };

  const handleAddAppointmentClick = (date: Date) => {
    resetAppointmentForm();
    setNewAppDate(format(date, "yyyy-MM-dd'T'HH:mm"));
    setIsModalOpen(true);
  };

  const handleSubmitAppointment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newAppCustomer || !newAppService || !newAppDate) return;
    setFormErrors({});
    setFormGeneralError('');
    setIsSaving(true);

    try {
      await createAdminAppointment({
        serviceId: parseInt(newAppService),
        customerName: newAppCustomer,
        customerPhone: newAppPhone,
        startTime: fromSalonWallClock(newAppDate).toISOString(),
        status: 'confirmed',
        staffId: newAppStaff ? parseInt(newAppStaff) : null,
        clientId: newAppClientId ?? undefined,
      });

      const appts = await refetchAppointments();
      setAppointments(appts);
      closeAppointmentModal();
    } catch (err) {
      if (err instanceof ApiError) {
        const fieldErrs: Record<string, string> = {};
        for (const [key, val] of Object.entries(err.body)) {
          if (Array.isArray(val)) fieldErrs[key] = val[0];
        }
        if (Object.keys(fieldErrs).length > 0) {
          setFormErrors(fieldErrs);
        } else {
          setFormGeneralError(err.message);
        }
      } else {
        setFormGeneralError('Something went wrong. Please try again.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="bg-canvas min-h-screen">
      <AdminNav
        tenantName={tenant.name}
        isOwner={isOwner}
        activeTab={effectiveTab}
        onSelect={setActiveTab}
        onSupport={() => setIsSupportModalOpen(true)}
        onLogout={handleLogout}
      />

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        {effectiveTab === 'calendar' ? (
          <>
            {/* Stats */}
            <div className="mb-8 grid grid-cols-1 gap-5 sm:grid-cols-3">
              <div className="border-default bg-surface overflow-hidden rounded-xl border shadow-sm">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <CalendarIcon className="text-fg-subtle h-6 w-6" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-fg-muted truncate text-sm font-medium">
                          Upcoming Appointments
                        </dt>
                        <dd className="text-fg text-2xl font-semibold">
                          {
                            appointments.filter(
                              (a) => a.status !== 'cancelled' && new Date(a.startTime) > new Date(),
                            ).length
                          }
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border-default bg-surface overflow-hidden rounded-xl border shadow-sm">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Users className="text-fg-subtle h-6 w-6" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-fg-muted truncate text-sm font-medium">
                          Total Clients
                        </dt>
                        <dd className="text-fg text-2xl font-semibold">
                          {
                            new Set(
                              appointments.map(
                                (a) => a.customer.id ?? `deleted:${a.customer.name}`,
                              ),
                            ).size
                          }
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
              <div className="border-default bg-surface overflow-hidden rounded-xl border shadow-sm">
                <div className="p-5">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <DollarSign className="text-fg-subtle h-6 w-6" />
                    </div>
                    <div className="ml-5 w-0 flex-1">
                      <dl>
                        <dt className="text-fg-muted truncate text-sm font-medium">Est. Revenue</dt>
                        <dd className="text-fg flex items-center gap-2 text-2xl font-semibold">
                          <span>
                            {showRevenue
                              ? `$${appointments
                                  .filter((app) => app.status !== 'cancelled')
                                  .reduce((sum, app) => sum + (Number(app.servicePrice) || 0), 0)
                                  .toFixed(2)}`
                              : '••••'}
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowRevenue((v) => !v)}
                            className="text-fg-subtle hover:bg-muted hover:text-fg-secondary rounded-md p-1 transition-colors"
                            aria-label={showRevenue ? 'Hide revenue' : 'Show revenue'}
                            title={showRevenue ? 'Hide revenue' : 'Show revenue'}
                          >
                            {showRevenue ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </button>
                        </dd>
                      </dl>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Calendar + Today's Agenda */}
            <div className="mb-8 flex flex-col gap-6 lg:flex-row">
              <div className="order-2 min-w-0 flex-1 lg:order-1">
                <AdminCalendar
                  tenant={tenant}
                  appointments={appointments}
                  services={services}
                  staff={isOwner ? staffList : []}
                  timeOff={timeOff}
                  canAddAppointment={isOwner}
                  onAddAppointment={handleAddAppointmentClick}
                  onAppointmentClick={handleAppointmentClick}
                />
              </div>
              <div className="order-1 w-full shrink-0 lg:order-2 lg:w-80">
                <TodayAgenda
                  appointments={appointments}
                  onAppointmentClick={handleAppointmentClick}
                />
              </div>
            </div>
          </>
        ) : effectiveTab === 'analytics' ? (
          <AdminAnalytics />
        ) : effectiveTab === 'clients' ? (
          <AdminClients />
        ) : (
          <AdminSettingsLayout activeTab={effectiveTab} onSelect={setActiveTab}>
            {effectiveTab === 'salon' ? (
              <AdminSalonSettings
                onServicesChanged={async () => {
                  if (tenant) {
                    const detail = await fetchTenantDetail(tenant.slug);
                    setServices(detail.services);
                    setStaffList(detail.staff);
                  }
                }}
              />
            ) : effectiveTab === 'staff' ? (
              <AdminStaffSettings salonHours={tenant.businessHours} />
            ) : effectiveTab === 'timeoff' ? (
              <AdminTimeOff
                onAppointmentsChanged={async () => {
                  // Cancelling bookings from the conflict dialog changes what the
                  // calendar tab should show.
                  setAppointments(await refetchAppointments());
                }}
                // Adding or lifting a block moves the calendar's banner too, and
                // switching tabs doesn't remount it — so take the list this tab
                // just loaded rather than fetching the same rows again.
                onTimeOffChanged={setTimeOff}
              />
            ) : effectiveTab === 'appearance' ? (
              <AdminAppearanceSettings />
            ) : (
              <AdminAccountSettings />
            )}
          </AdminSettingsLayout>
        )}
      </div>

      {/* Support Modal */}
      {isSupportModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          onClick={() => setIsSupportModalOpen(false)}
        >
          <div
            className="animate-in fade-in zoom-in bg-surface w-full max-w-md overflow-hidden rounded-2xl shadow-xl duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="border-subtle flex items-center justify-between border-b px-6 py-4">
              <h3 className="text-fg text-lg font-bold">Contact Support</h3>
              <button
                onClick={() => setIsSupportModalOpen(false)}
                className="hover:bg-muted rounded-full p-2 transition-colors"
              >
                <X className="text-fg-subtle h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 p-6">
              <p className="text-fg-secondary text-sm leading-6">
                Need help with your salon dashboard? Open WhatsApp and reach our support team.
              </p>
              <div className="border-default bg-canvas rounded-xl border p-4">
                <p className="text-fg-muted text-sm font-medium">WhatsApp number</p>
                <a
                  href={SUPPORT_WHATSAPP_URL}
                  target="_blank"
                  rel="noreferrer"
                  className="text-accent mt-1 inline-flex text-lg font-semibold hover:underline"
                >
                  {SUPPORT_WHATSAPP_NUMBER}
                </a>
              </div>
              <a
                href={SUPPORT_WHATSAPP_URL}
                target="_blank"
                rel="noreferrer"
                className="bg-brand-600 hover:bg-brand-700 flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 font-semibold text-white transition-colors"
              >
                <MessageCircle className="h-4 w-4" />
                Open WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Add Appointment Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="animate-in fade-in zoom-in bg-surface w-full max-w-md overflow-hidden rounded-2xl shadow-xl duration-200">
            <div className="border-subtle flex items-center justify-between border-b px-6 py-4">
              <h3 className="text-fg text-lg font-bold">New Appointment</h3>
              <button
                onClick={closeAppointmentModal}
                className="hover:bg-muted rounded-full p-2 transition-colors"
              >
                <X className="text-fg-subtle h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmitAppointment} className="space-y-4 p-6">
              {formGeneralError && (
                <div className="border-danger/25 bg-danger-soft text-danger rounded-lg border px-3 py-2 text-sm">
                  {formGeneralError}
                </div>
              )}
              <div className="relative">
                <label className="text-fg-secondary mb-1 block text-sm font-semibold">
                  Customer Name
                </label>
                <input
                  ref={customerInputRef}
                  type="text"
                  required
                  autoComplete="off"
                  value={newAppCustomer}
                  onChange={(e) => handleCustomerInputChange(e.target.value)}
                  onFocus={() => {
                    if (customerSuggestions.length > 0 && newAppCustomer.trim().length >= 2) {
                      setShowSuggestions(true);
                    }
                  }}
                  className={`focus:ring-brand-600 w-full rounded-lg border px-4 py-2 transition-all outline-none focus:border-transparent focus:ring-2 ${formErrors.customerName ? 'border-danger/40' : 'border-default'}`}
                  placeholder="John Doe"
                />
                {showSuggestions && customerSuggestions.length > 0 && (
                  <div
                    ref={suggestionsRef}
                    className="border-default bg-surface absolute z-10 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border shadow-lg"
                  >
                    {customerSuggestions.map((c) => (
                      <button
                        type="button"
                        key={c.id}
                        onClick={() => {
                          setNewAppCustomer(c.name);
                          setNewAppPhone(c.phone || undefined);
                          setNewAppClientId(c.id);
                          setShowSuggestions(false);
                          setCustomerSuggestions([]);
                        }}
                        className="hover:bg-canvas flex w-full items-center justify-between px-4 py-2 text-left text-sm transition-colors"
                      >
                        <span className="text-fg font-medium">{c.name}</span>
                        {c.phone && <span className="text-fg-muted ml-2">{c.phone}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {formErrors.customerName && (
                  <p className="text-danger mt-1 text-xs">{formErrors.customerName}</p>
                )}
              </div>
              <div>
                <label className="text-fg-secondary mb-1 block text-sm font-semibold">
                  Phone Number <span className="text-fg-muted font-normal">(optional)</span>
                </label>
                <PhoneField
                  value={newAppPhone}
                  onChange={(v) => {
                    setNewAppPhone(v);
                    setNewAppClientId(null);
                  }}
                  selectClassName="px-3 py-2 border border-default rounded-lg focus:ring-2 focus:ring-brand-600 focus:border-transparent outline-none transition-all"
                  inputClassName={`flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-brand-600 focus:border-transparent outline-none transition-all ${formErrors.customerPhone ? 'border-danger/40' : 'border-default'}`}
                />
                {formErrors.customerPhone && (
                  <p className="text-danger mt-1 text-xs">{formErrors.customerPhone}</p>
                )}
              </div>
              <div>
                <label className="text-fg-secondary mb-1 block text-sm font-semibold">
                  Service
                </label>
                <select
                  required
                  value={newAppService}
                  onChange={(e) => {
                    const serviceId = e.target.value;
                    setNewAppService(serviceId);
                    // Drop a stale worker pick: if the previously selected worker
                    // doesn't perform the newly chosen service, reset to "Any
                    // available" so a hidden, filtered-out worker can't be submitted.
                    setNewAppStaff((cur) => {
                      if (!cur) return cur;
                      const worker = staffList.find((w) => w.id === parseInt(cur));
                      return worker && worker.serviceIds.includes(parseInt(serviceId)) ? cur : '';
                    });
                  }}
                  className={`focus:ring-brand-600 w-full rounded-lg border px-4 py-2 transition-all outline-none focus:border-transparent focus:ring-2 ${formErrors.serviceId ? 'border-danger/40' : 'border-default'}`}
                >
                  <option value="">Select a service</option>
                  {services.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} (${s.price})
                    </option>
                  ))}
                </select>
                {formErrors.serviceId && (
                  <p className="text-danger mt-1 text-xs">{formErrors.serviceId}</p>
                )}
              </div>
              {staffList.length > 0 && (
                <div>
                  <label className="text-fg-secondary mb-1 block text-sm font-semibold">
                    Worker
                  </label>
                  <select
                    value={newAppStaff}
                    onChange={(e) => setNewAppStaff(e.target.value)}
                    className={`focus:ring-brand-600 w-full rounded-lg border px-4 py-2 transition-all outline-none focus:border-transparent focus:ring-2 ${formErrors.staffId ? 'border-danger/40' : 'border-default'}`}
                  >
                    <option value="">Any available</option>
                    {staffList
                      .filter(
                        (w) => !newAppService || w.serviceIds.includes(parseInt(newAppService)),
                      )
                      .map((w) => (
                        <option key={w.id} value={w.id}>
                          {w.name}
                        </option>
                      ))}
                  </select>
                  {formErrors.staffId && (
                    <p className="text-danger mt-1 text-xs">{formErrors.staffId}</p>
                  )}
                </div>
              )}
              <div>
                <label className="text-fg-secondary mb-1 block text-sm font-semibold">
                  Date & Time
                </label>
                <input
                  type="datetime-local"
                  required
                  value={newAppDate}
                  onChange={(e) => setNewAppDate(e.target.value)}
                  className={`focus:ring-brand-600 w-full rounded-lg border px-4 py-2 transition-all outline-none focus:border-transparent focus:ring-2 ${formErrors.startTime ? 'border-danger/40' : 'border-default'}`}
                />
                {formErrors.startTime && (
                  <p className="text-danger mt-1 text-xs">{formErrors.startTime}</p>
                )}
                {/* Suppressed once the server has spoken about this field: the two
                    say the same thing, and the server's version is the authority. */}
                {!formErrors.startTime && timeOffClash && (
                  <div className="border-warn/30 bg-warn-soft mt-2 flex items-start gap-2 rounded-lg border px-3 py-2">
                    <CalendarOff className="text-warn mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <p className="text-warn text-xs">
                      {timeOffClash.message}{' '}
                      <span className="font-semibold">{formatTimeOffSpan(timeOffClash.block)}</span>
                    </p>
                  </div>
                )}
              </div>
              <button
                type="submit"
                disabled={isSaving}
                className="bg-brand-600 hover:bg-brand-700 mt-4 flex w-full items-center justify-center gap-2 rounded-xl py-3 font-bold text-white transition-colors disabled:opacity-50"
              >
                {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                {isSaving ? 'Creating...' : 'Create Appointment'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Appointment Detail Modal */}
      {selectedAppointment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
          <div className="animate-in fade-in zoom-in bg-surface w-full max-w-md overflow-hidden rounded-2xl shadow-xl duration-200">
            <div className="border-subtle flex items-center justify-between border-b px-6 py-4">
              <h3 className="text-fg text-lg font-bold">Appointment Details</h3>
              <button
                onClick={closeDetailModal}
                className="hover:bg-muted rounded-full p-2 transition-colors"
              >
                <X className="text-fg-subtle h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4 p-6">
              {actionError && (
                <div className="border-danger/25 bg-danger-soft text-danger rounded-lg border px-3 py-2 text-sm">
                  {actionError}
                </div>
              )}
              <div className="bg-canvas space-y-3 rounded-xl p-4">
                <div className="flex flex-col gap-0.5 text-sm sm:flex-row sm:justify-between">
                  <span className="text-fg-muted">Customer</span>
                  <span className="text-fg font-medium break-words">
                    {selectedAppointment.customer.name}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5 text-sm sm:flex-row sm:justify-between">
                  <span className="text-fg-muted shrink-0">Phone</span>
                  <span className="text-fg font-medium">{selectedAppointment.customer.phone}</span>
                </div>
                <div className="flex flex-col gap-0.5 text-sm sm:flex-row sm:justify-between">
                  <span className="text-fg-muted">Service</span>
                  <span className="text-fg font-medium">
                    {services.find((s) => s.id === selectedAppointment.serviceId)?.name ||
                      'Unknown'}
                  </span>
                </div>
                {selectedAppointment.staffName && (
                  <div className="flex flex-col gap-0.5 text-sm sm:flex-row sm:justify-between">
                    <span className="text-fg-muted">Worker</span>
                    <span className="text-fg font-medium">{selectedAppointment.staffName}</span>
                  </div>
                )}
                <div className="flex flex-col gap-0.5 text-sm sm:flex-row sm:justify-between">
                  <span className="text-fg-muted">Status</span>
                  <span
                    className={`font-medium capitalize ${STATUS_TEXT[selectedAppointment.status]}`}
                  >
                    {selectedAppointment.status}
                  </span>
                </div>
                <div className="border-default border-t pt-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-fg-muted">Date & Time</span>
                    {isOwner &&
                      selectedAppointment.status !== 'cancelled' &&
                      selectedAppointment.status !== 'completed' &&
                      !isEditing && (
                        <button
                          onClick={() => setIsEditing(true)}
                          className="hover:text-accent text-fg-subtle p-1 transition-colors"
                          title="Edit date & time"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                  </div>
                  {isEditing ? (
                    <div className="mt-2 space-y-3">
                      <input
                        type="datetime-local"
                        value={editDateTime}
                        onChange={(e) => setEditDateTime(e.target.value)}
                        className="focus:ring-brand-600 border-default w-full min-w-0 rounded-lg border px-3 py-2 text-sm transition-all outline-none focus:border-transparent focus:ring-2 sm:px-4"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setIsEditing(false);
                            setEditDateTime(
                              format(
                                toSalonTime(selectedAppointment.startTime),
                                "yyyy-MM-dd'T'HH:mm",
                              ),
                            );
                          }}
                          disabled={isSaving}
                          className="border-default text-fg-secondary hover:bg-canvas flex-1 rounded-lg border px-3 py-2 text-xs font-semibold transition-colors"
                        >
                          Discard
                        </button>
                        <button
                          onClick={handleSaveDateTime}
                          disabled={
                            isSaving ||
                            editDateTime ===
                              format(
                                toSalonTime(selectedAppointment.startTime),
                                "yyyy-MM-dd'T'HH:mm",
                              )
                          }
                          className="bg-brand-600 hover:bg-brand-700 flex-1 rounded-lg px-3 py-2 text-xs font-semibold text-white transition-colors disabled:opacity-50"
                        >
                          {isSaving ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-fg mt-1 text-sm font-medium">
                      {format(
                        toSalonTime(selectedAppointment.startTime),
                        "MMM d, yyyy 'at' h:mm a",
                      )}
                    </p>
                  )}
                </div>
              </div>

              {selectedAppointment.status !== 'cancelled' &&
                selectedAppointment.status !== 'completed' && (
                  <div className="border-subtle border-t pt-4">
                    <button
                      onClick={() => setShowCancelConfirm(true)}
                      disabled={isSaving}
                      className="border-danger/25 text-danger hover:bg-danger-soft w-full rounded-xl border px-4 py-3 text-sm font-bold transition-colors disabled:opacity-50"
                    >
                      Cancel Appointment
                    </button>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* Cancel Appointment Confirmation */}
      <ConfirmDialog
        open={showCancelConfirm}
        title="Cancel Appointment"
        message={`Are you sure you want to cancel ${selectedAppointment?.customer.name}'s appointment? This action cannot be undone.`}
        confirmLabel={isSaving ? 'Cancelling...' : 'Cancel Appointment'}
        variant="danger"
        busy={isSaving}
        onConfirm={handleCancelAppointment}
        onCancel={() => setShowCancelConfirm(false)}
      />
    </div>
  );
}
